import { useQuery } from '@tanstack/react-query';
import {
  fetchUserWorkspaces,
  fetchWorkspacePosts,
} from '../api/workspaceApi';
import {
  type Account,
  type Media,
  type Subscription,
  type Workspace,
  type Post,
  Platform,
  type WorkspaceRole,
  type JoinRequest,
} from '../types';

interface WorkspaceDataOptions {
  includeJoinRequests?: boolean;
}

const createEmptyAccounts = (): Record<Platform, Account[]> => ({
  [Platform.Facebook]: [],
  [Platform.Instagram]: [],
  [Platform.X]: [],
  [Platform.YouTube]: [],
  [Platform.Mastodon]: [],
  [Platform.Threads]: [],
  [Platform.Bluesky]: [],
  [Platform.TikTok]: [],
});

export const useWorkspaceData = (workspace: Workspace | null, options: WorkspaceDataOptions = {}) => {
  const workspaceId = workspace?.id;

  // 1. Fetch Posts
  const { data: posts, isLoading: postsLoading } = useQuery({
    queryKey: ['workspace', workspaceId, 'posts'],
    queryFn: () => (workspaceId ? fetchWorkspacePosts(workspaceId) : Promise.resolve([])),
    enabled: !!workspaceId,
    refetchInterval: 5000,
  });

  // 2. Fetch Media (TODO: Add API endpoint)
  const { data: mediaLibrary, isLoading: mediaLoading } = useQuery({
    queryKey: ['workspace', workspaceId, 'media'],
    queryFn: () => Promise.resolve([] as Media[]), // Placeholder
    enabled: !!workspaceId,
  });

  // 3. Fetch Accounts (TODO: Add API endpoint)
  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['workspace', workspaceId, 'accounts'],
    queryFn: () => Promise.resolve(createEmptyAccounts()), // Placeholder
    enabled: !!workspaceId,
  });

  // 4. Fetch Meta (Subscription, Roles) - For now reusing workspace object data
  // In a real app we might fetch this fresh if the workspace object is stale
  
  const loading = postsLoading || mediaLoading || accountsLoading;

  return {
    isReady: !loading && !!workspace,
    posts: posts ?? [],
    mediaLibrary: mediaLibrary ?? [],
    accounts: accounts ?? createEmptyAccounts(),
    subscription: workspace?.subscription ?? null,
    memberRoles: workspace?.memberRoles ?? {},
    joinRequests: [] as JoinRequest[], // Placeholder
  };
};
